import { createFileRoute } from '@tanstack/react-router'
import { useEvent, useEventTerminals } from '@/lib/api/queries'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { CreditCard, Smartphone, Wifi, Printer, Ticket, Loader2, RefreshCw, Settings, Sparkles } from 'lucide-react'
import { usePermission } from '@/lib/permissions'
import { AccessDenied } from '@/components/permission-guard'
import { Button } from '@/components/ui/button'
import { useEffect, useState } from 'react'
import { PrinterSelectionModal } from '@/features/printer/components/PrinterSelectionModal'
import { getPrinterSettings } from '@/lib/printer/settings'
import { printerService } from '@/lib/printer'
import { useIntegrations, useSyncIntegration } from '@/lib/api/provider-queries'
import { IntegrationDetail } from '@/features/providers/components/IntegrationDetail'
import { ProviderSetupWizard } from '@/features/providers/components/ProviderSetupWizard'
import { PROVIDER_SCHEMAS, PROVIDER_TYPES } from '@/lib/providers/schema'
import type { Integration, ProviderType } from '@/lib/api/provider-types'

export const Route = createFileRoute('/_authenticated/events/$eventId/integrations')({
  component: IntegrationsPage,
})

function IntegrationsPage() {
  const { eventId } = Route.useParams()
  const eventIdNum = Number(eventId)
  const [showPrinterModal, setShowPrinterModal] = useState(false)
  const [wizardProvider, setWizardProvider] = useState<ProviderType | null>(null)
  const [selectedIntegration, setSelectedIntegration] = useState<Integration | null>(null)
  const [printerSettings, setPrinterSettingsState] = useState(getPrinterSettings())
  const [printerState, setPrinterState] = useState(printerService.getState())

  // Permission check
  const canViewIntegrations = usePermission('can_view_integrations')

  const { data: event } = useEvent(eventIdNum)
  const { data: integrationsData, isLoading: integrationsLoading } = useIntegrations(eventIdNum)
  const { data: terminals } = useEventTerminals(eventIdNum)
  const integrations = integrationsData?.results ?? []
  const syncMutation = useSyncIntegration()

  // Group integrations by provider type
  const integrationsByProvider = (providerType: ProviderType) =>
    integrations.filter(i => i.provider_type === providerType)

  // Get organization ID from event
  const organizationId = event?.organization
    ? typeof event.organization === 'object' ? event.organization.id : event.organization
    : 0

  useEffect(() => {
    setPrinterSettingsState(getPrinterSettings())
  }, [])

  useEffect(() => {
    const unsubscribe = printerService.subscribe(setPrinterState)
    return unsubscribe
  }, [])

  // Permission guard
  if (!canViewIntegrations) {
    return <AccessDenied message="You don't have permission to view integrations." />
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-neutral-900 dark:text-white">
          Integrations
        </h1>
        {event && (
          <p className="text-neutral-600 dark:text-neutral-400 mt-1">
            {event.name}
          </p>
        )}
      </div>

      {/* Device Integration Cards */}
      <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
        <Card>
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                <Printer className="w-6 h-6 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <CardTitle className="text-lg">Printers</CardTitle>
                <CardDescription>Receipt printing</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-neutral-600 dark:text-neutral-400 mb-4">
              Manage print method and connected printers for this device
            </p>
            <div className="space-y-2 mb-4 text-sm text-neutral-600 dark:text-neutral-400">
              <div>
                Method: <span className="font-medium text-neutral-900 dark:text-neutral-200">{printerSettings.preferredConnection}</span>
              </div>
              {printerSettings.preferredConnection !== 'system' && (
                <div>
                  Status: <span className="font-medium text-neutral-900 dark:text-neutral-200">
                    {printerState.device?.name || 'Not connected'}
                  </span>
                </div>
              )}
            </div>
            <Button type="button" variant="outline" onClick={() => setShowPrinterModal(true)}>
              Manage Printers
            </Button>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-lg">
                <CreditCard className="w-6 h-6 text-green-600 dark:text-green-400" />
              </div>
              <div>
                <CardTitle className="text-lg">Card Terminals</CardTitle>
                <CardDescription>Card-present payments</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-neutral-600 dark:text-neutral-400 mb-4">
              Terminals available to this event — matched by its merchant account.
              Provisioning is managed by EventPay; charge cards from the Reload page.
            </p>
            {terminals && terminals.length > 0 ? (
              <ul className="space-y-2 text-sm">
                {terminals.map((device) => (
                  <li key={device.id} className="flex items-center justify-between gap-2">
                    <span className="font-medium text-neutral-900 dark:text-neutral-200 truncate">
                      {device.name}
                      <span className="ml-2 text-xs font-normal text-neutral-500 dark:text-neutral-400">
                        {device.device_model}
                      </span>
                    </span>
                    <span className={`inline-flex items-center gap-1.5 text-xs ${
                      device.last_connection === 'open'
                        ? 'text-green-600 dark:text-green-400'
                        : 'text-neutral-500 dark:text-neutral-400'
                    }`}>
                      <span className={`w-2 h-2 rounded-full ${
                        device.last_connection === 'open' ? 'bg-green-500' : 'bg-neutral-400'
                      }`} />
                      {device.last_connection === 'open' ? 'Connected' : device.status}
                    </span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-neutral-500 dark:text-neutral-400">
                No terminals are provisioned for this event&apos;s merchant account.
              </p>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                <CreditCard className="w-6 h-6 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <CardTitle className="text-lg">Payment Devices</CardTitle>
                <CardDescription>Payment card readers</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-neutral-600 dark:text-neutral-400 mb-4">
              Manage connected payment terminals and card readers
            </p>
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-neutral-100 text-neutral-700 dark:bg-neutral-800 dark:text-neutral-400">
              Not configured
            </span>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
                <Smartphone className="w-6 h-6 text-purple-600 dark:text-purple-400" />
              </div>
              <div>
                <CardTitle className="text-lg">Mobile App</CardTitle>
                <CardDescription>EventPay App</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-neutral-600 dark:text-neutral-400 mb-4">
              Configure mobile app settings and features
            </p>
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
              Active
            </span>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-100 dark:bg-orange-900/30 rounded-lg">
                <Wifi className="w-6 h-6 text-orange-600 dark:text-orange-400" />
              </div>
              <div>
                <CardTitle className="text-lg">Webhooks</CardTitle>
                <CardDescription>Event notifications</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-neutral-600 dark:text-neutral-400 mb-4">
              Set up webhooks for real-time event notifications
            </p>
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-neutral-100 text-neutral-700 dark:bg-neutral-800 dark:text-neutral-400">
              Not configured
            </span>
          </CardContent>
        </Card>
      </div>

      {/* Ticketing Providers Section */}
      <div className="mt-10">
        <div className="flex items-center gap-2 mb-4">
          <Ticket className="w-5 h-5 text-neutral-700 dark:text-neutral-300" />
          <h2 className="text-lg font-semibold text-neutral-900 dark:text-white">
            Ticketing Providers
          </h2>
        </div>

        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {PROVIDER_TYPES.map(providerType => {
            const schema = PROVIDER_SCHEMAS[providerType]
            const providerIntegrations = integrationsByProvider(providerType)
            const isConnected = providerIntegrations.length > 0
            const isNative = schema.native === true

            return (
              <Card key={providerType} className={`flex flex-col${isNative ? ' border-indigo-200 dark:border-indigo-800 ring-1 ring-indigo-100 dark:ring-indigo-900/40' : ''}`}>
                <CardHeader className="pb-3">
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-lg ${isNative ? 'bg-indigo-200/60 dark:bg-indigo-900/50' : 'bg-indigo-100 dark:bg-indigo-900/30'}`}>
                      <Ticket className={`w-6 h-6 ${isNative ? 'text-indigo-700 dark:text-indigo-300' : 'text-indigo-600 dark:text-indigo-400'}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <CardTitle className="text-lg">{schema.label}</CardTitle>
                        {isConnected && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
                            Connected
                          </span>
                        )}
                      </div>
                      <CardDescription>{schema.description}</CardDescription>
                    </div>
                  </div>
                  {schema.perks && schema.perks.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {schema.perks.map(perk => (
                        <span key={perk} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400">
                          <Sparkles className="w-3 h-3" />
                          {perk}
                        </span>
                      ))}
                    </div>
                  )}
                </CardHeader>
                <CardContent className="mt-auto">
                  {integrationsLoading ? (
                    <Loader2 className="w-4 h-4 animate-spin text-neutral-400" />
                  ) : isConnected ? (
                    <div className="space-y-3">
                      {providerIntegrations.map(integration => {
                        const isSyncing = integration.sync_status === 'syncing' ||
                          (syncMutation.isPending && syncMutation.variables === integration.id)

                        return (
                          <div key={integration.id} className="space-y-2">
                            {integration.last_synced_at && (
                              <span className="text-xs text-neutral-500 dark:text-neutral-400">
                                Synced {new Date(integration.last_synced_at).toLocaleDateString()}
                              </span>
                            )}
                            <p className="text-sm text-neutral-700 dark:text-neutral-300 font-medium truncate">
                              Event: {integration.external_event_name}
                            </p>
                            <div className="flex items-center gap-2">
                              <Button type="button" variant="outline" size="sm" onClick={() => setSelectedIntegration(integration)}>
                                <Settings className="w-3.5 h-3.5 mr-1" />
                                Configure
                              </Button>
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                disabled={isSyncing}
                                onClick={() => syncMutation.mutate(integration.id)}
                              >
                                {isSyncing
                                  ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" />
                                  : <RefreshCw className="w-3.5 h-3.5 mr-1" />
                                }
                                Sync
                              </Button>
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  ) : (
                    <div>
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-neutral-100 text-neutral-700 dark:bg-neutral-800 dark:text-neutral-400 mb-3">
                        Not connected
                      </span>
                      <div className="mt-3">
                        <Button type="button" variant="outline" onClick={() => setWizardProvider(providerType)}>
                          Connect {schema.label}
                        </Button>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            )
          })}
        </div>
      </div>

      {/* Modals */}
      <PrinterSelectionModal
        isOpen={showPrinterModal}
        onClose={() => setShowPrinterModal(false)}
      />

      <ProviderSetupWizard
        isOpen={wizardProvider !== null}
        onClose={() => setWizardProvider(null)}
        eventId={eventIdNum}
        organizationId={organizationId}
        initialProvider={wizardProvider ?? undefined}
      />

      {selectedIntegration && (
        <IntegrationDetail
          integration={selectedIntegration}
          eventId={eventIdNum}
          onClose={() => setSelectedIntegration(null)}
        />
      )}
    </div>
  )
}
